import { apyToAprPercent } from '../../../utils/parsing'
import type { YieldFetcher, YieldResult } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Native Credit Pool (wNLP) supply APR.
 *
 * Native publishes no unauthenticated pool API — the FirmQuote/pool
 * endpoints are all `apikey`-gated — and the wNLP exchange rate is an
 * operator-pushed rebase, so an on-chain APR would need an archival
 * rate-delta (the same blocker as stCELO). DefiLlama tracks the pools
 * under `project=native-credit-pool`, which is the only free source.
 *
 * `/pools` is a ~11 MB unfiltered payload and DefiLlama exposes no
 * server-side project filter, so pool UUIDs are pinned here and read
 * one-by-one through the shared `poolsEnriched?pool=` helper. Fetchers
 * are built **per chain** so a chain's fetch only issues requests for
 * its own pools (≤13, in parallel) — and the four chains DefiLlama does
 * not index issue none at all.
 *
 * Coverage is partial by design: 23 of the 108 registry entries have a
 * DefiLlama pool. The rest — the tokenized-equity / RWA wNLPs and every
 * pool on Monad, X Layer, Robinhood and Morph — report `supplyRate: 0`
 * until DefiLlama lists them. Zero here means "unknown", not "no yield";
 * the vault's `exchangeRate` still shows accrual to date.
 *
 * Re-derive the map with:
 *   `curl -s https://yields.llama.fi/pools | jq '[.data[]
 *      | select(.project=="native-credit-pool")
 *      | {chain,symbol,pool,u:.underlyingTokens[0]}]'`
 */

/** `${chainId}:${underlyingAddressLowercase}` — the registry's `yieldKey`. */
export const nativeCreditPoolYieldKey = (
  chainId: string,
  underlying: string,
): string => `${chainId}:${underlying.toLowerCase()}`

/**
 * DefiLlama pool UUID per `chainId → underlying`. Keyed by the pool's
 * underlying (not the wNLP share token) because that is what DefiLlama
 * reports in `underlyingTokens`, and it is stable across share-token
 * redeploys. APYs in the comments are the values at pinning time —
 * sanity anchors, not assertions.
 */
const POOL_IDS: { [chainId: string]: { [underlying: string]: string } } = {
  // Ethereum
  '1': {
    // wNLP-USDT — 2.92 % APY at pinning (2026-07-30)
    '0xdac17f958d2ee523a2206206994597c13d831ec7':
      'ea6b472d-82a2-47fa-9c55-78907deebe13',
    // wNLP-USDC — 2.58 %
    '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48':
      '8e92a8ea-0dbd-463e-bb0b-a7f419b20db5',
    // wNLP-WETH — 2.21 %
    '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2':
      '76aa8b2e-c5cd-4ae5-8182-c74ee969d71d',
    // wNLP-WBTC — 0.43 %
    '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599':
      '8c603090-cda6-4a17-975a-69a08a7dcf5e',
    // wNLP-tGLD — 0 %
    '0x6a547b25534234bb79ce6961a23db13de154b6f4':
      'eb208601-24b8-5291-8c13-1327208ef2e9',
    // wNLP-STONEUSD — 0 %
    '0x6a6e3a4396993a4ec98a6f4a654cc0819538721e':
      '09f09cf1-915a-4b71-82eb-5f6c54f0cc68',
  },
  // BNB Chain
  '56': {
    // wNLP-Q3B (QQQB) — 36.42 % on a $4.2 M tokenized-equity pool
    '0x205812cdbed920aff76c6580abd681a46d11efc7':
      'a5bd33df-4410-5631-a44b-bab8214b1dc2',
    // wNLP-USDT — 3.40 %
    '0x55d398326f99059ff775485246999027b3197955':
      '14ca42f2-1d51-4093-b5c8-74effe4c5cf3',
    // wNLP-WBNB — 1.17 %
    '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c':
      'cd637956-e278-4462-a030-8d0d26a63a5c',
    // wNLP-P4B (PLTRB) — 0 %
    '0x0ca5d51d0277bd006fd9607d3e560785ebad8222':
      'a36f61e0-31d4-5bce-85c1-b7cfd57b2c21',
    // wNLP-S4B (SNDKB) — 0 %
    '0x3ee4df61bd4f867e349beae8bfe07bc31b4850fb':
      '06081a23-0157-4141-951b-eae858070214',
    // wNLP-T4B (TSLAB) — 0 %
    '0x5b1910eaad6450e50f816082aa078c41f10c292f':
      '44b167ce-b22c-4294-8877-4f71042bdd13',
    // wNLP-L4B (LITEB) — 0 %
    '0x64748bea17b6d19e242adf20425de2440c656142':
      'aa632ad9-f1e1-5b35-a76a-33006320aa1e',
    // wNLP-M5B (METAB) — 0 %
    '0x7425889fe94f9d693e8daefe88bcced6acfef4c0':
      '75edd0e5-82b9-5ff8-beba-4d9f07322990',
    // wNLP-M6B (MSFTB) — 0 %
    '0x80106cb3ead06659a5ad19df39d9b4733863b9b0':
      '8d6849a9-6bcb-550d-b916-38a0e792a097',
    // wNLP-C4B (CRCLB) — 0 %
    '0x80f3d493ebce97e343c53d29a137942416b4ffc0':
      'a1e52090-7faf-46e9-9e1e-174982f4cad9',
    // wNLP-USDC — 0 %
    '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d':
      '0f76c834-6f24-4187-b7d5-6f3d641c3123',
    // wNLP-USD1 — 0 %
    '0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d':
      '28211a21-46c4-4a0d-807a-f010b304c3d0',
    // wNLP-M2B (MUB) — 0 %
    '0xcdf2f3e0fa43c47a6662a91c9e4a7c5f69762699':
      '1687633e-61ff-49d4-9256-5734ba06e595',
  },
  // Arbitrum
  '42161': {
    // wNLP-USDT (USD₮0) — 2.57 %
    '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9':
      'a6dc5af9-ff47-44f4-ad38-a0c6fc62278e',
    // wNLP-WETH — 0.88 %
    '0x82af49447d8a07e3bd95bd0d56f35241523fbab1':
      '1b7193de-1ddf-48b7-81d2-13f082a8cf3a',
  },
  // Base
  '8453': {
    // wNLP-USDC — 3.52 %
    '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913':
      'a057d9d8-99d4-4017-bb59-b96f67366178',
    // wNLP-WETH — 1.98 %
    '0x4200000000000000000000000000000000000006':
      '9f1d3455-050c-482c-93e0-e1271d240cec',
  },
}

/**
 * One fetcher per chain, built once at module load.
 *
 * The identity matters: `resolveEntryApr` dedups by fetcher **reference**,
 * so every registry entry on a chain must share one instance for that
 * chain's pools to be fetched exactly once per request.
 */
const buildChainFetcher = (
  chainId: string,
  pools: { [underlying: string]: string },
): YieldFetcher => ({
  label: `NATIVE_CREDIT_POOL_${chainId}`,
  fetch: async () => {
    const rows = Object.entries(pools)
    const settled = await Promise.allSettled(
      rows.map(([, poolId]) => fetchDefiLlamaApy(poolId)),
    )
    const out: YieldResult = {}
    settled.forEach((res, i) => {
      // A single dead pool must not blank the whole chain — an entry with
      // no resolved key falls through to `0` in `resolveEntryApr`.
      if (res.status !== 'fulfilled') return
      if (!Number.isFinite(res.value)) return
      out[nativeCreditPoolYieldKey(chainId, rows[i][0])] = apyToAprPercent(
        res.value,
      )
    })
    return out
  },
})

const FETCHERS: { [chainId: string]: YieldFetcher } = Object.fromEntries(
  Object.entries(POOL_IDS).map(([chainId, pools]) => [
    chainId,
    buildChainFetcher(chainId, pools),
  ]),
)

/**
 * The stable per-chain fetcher, or `undefined` when DefiLlama does not
 * index that chain's Native pools (Monad / X Layer / Robinhood / Morph).
 * Registry entries without a fetcher report `supplyRate: 0`.
 */
export const nativeCreditPoolFetcher = (
  chainId: string,
): YieldFetcher | undefined => FETCHERS[chainId]

/** True when `chainId`/`underlying` has a pinned DefiLlama pool. */
export const hasNativeCreditPoolYield = (
  chainId: string,
  underlying: string,
): boolean => POOL_IDS[chainId]?.[underlying.toLowerCase()] !== undefined
